/**
 * API クライアント
 * AxiosベースのHTTPクライアント設定
 */

import axios from 'axios'
import { createClient } from './supabase/client'
import { getEnv } from '@/shared/lib/env'

const baseURL = import.meta.env.VITE_API_BASE_URL || getEnv('VITE_API_FALLBACK_URL') || 'http://127.0.0.1:8000/api/v1/'

if (import.meta.env.DEV) {
  console.info('[API Client] Initializing with baseURL:', baseURL)
}

const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: false,
})

/**
 * リクエストインターセプター
 */
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const supabase = createClient()
      const { data: { session }, error } = await supabase.auth.getSession()

      if (error) {
        console.warn('[API Client] Session error in request interceptor:', error)
        return config
      }

      if (session?.access_token) {
        const now = Math.floor(Date.now() / 1000)
        const bufferTime = 300
        if (session.expires_at && session.expires_at < (now + bufferTime)) {
          console.warn('[API Client] Session expired before request. Attempting refresh...')
          try {
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
            if (refreshError || !refreshData.session) {
              console.warn('[API Client] Session refresh failed. Clearing session.')
              await supabase.auth.signOut()
              return config
            }
            config.headers.Authorization = `Bearer ${refreshData.session.access_token}`
            return config
          } catch (refreshError) {
            console.error('[API Client] Session refresh error:', refreshError)
            await supabase.auth.signOut()
            return config
          }
        }

        config.headers.Authorization = `Bearer ${session.access_token}`
      }

      return config
    } catch (error) {
      console.error('[API Client] Request Interceptor Error:', error)
      return config
    }
  },
  (error) => {
    console.error('[API Client] Request Interceptor Error:', error)
    return Promise.reject(error)
  }
)

/**
 * レスポンスインターセプター
 */
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    console.error('[API Client] API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message
    })

    if (error.response?.status === 401) {
      console.warn('[API Client] Authentication error detected.')

      if (typeof window !== 'undefined') {
        const supabase = createClient()

        supabase.auth.signOut().then(() => {
          console.log('[API Client] Session cleared due to 401 error')
          const currentPath = window.location.pathname + window.location.search
          if (currentPath !== '/login') {
            localStorage.setItem('redirectAfterLogin', currentPath)
          }
          window.location.href = '/login'
        }).catch((signOutError: unknown) => {
          console.error('[API Client] Error during sign out:', signOutError)
          window.location.href = '/login'
        })
      }
    }

    if (error.code === 'ECONNREFUSED' || error.message?.includes('Network Error')) {
      console.warn('[API Client] Connection error detected:', error.message)
      error.isConnectionError = true
    }

    return Promise.reject(error)
  }
)

export default apiClient 